/* eslint-disable react/jsx-key */

import { useEffect, useState } from 'react';

import Image from 'next/image';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import SeturLogo from '@assets/images/60yil-footer.png';
import { SocialMediaIconsViewModel } from '@api/index';
import AppStoreLogo from '@assets/images/appstore.png';
import GooglePlayLogo from '@assets/images/googleplay.png';
import VisaLogo from '@assets/images/visa-mastercard-logo-2-x.png';
import KocHolding from '@assets/images/koc-holding.png';
import Tursab from '@assets/images/Tursab.png';
import { PhoneIcon, RightArrowIcon } from '@assets/icons';
import { Layout } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { addMenuClickEventsToGTM } from '@utils/gtm/menu-click-events-gtm';
import { addSocialMediaClickEventsToGTM } from '@utils/gtm/social-media-gtm';
import { addContactUsClickEventsToGTM } from '@utils/gtm/contact-us-gtm';
import { useFooterContext } from '@contexts/footer';
import { ETBIS_INFO } from '@constants/constants';
import { productImpression } from '@utils/newDataLayer/productImpression';
import { FooterViewModel } from '@api/v3';

import {
  BoxLabel,
  BoxText,
  TermsOfUse,
  FooterAboutCompany,
  FooterContent,
  FooterItem,
  FooterLineDivider,
  FooterListsItem,
  FooterListsItemLink,
  FooterListsItemTitle,
  FooterListsSumItem,
  FooterListsSumItemLink,
  FooterListsWrapper,
  FooterLogo,
  FooterMetaEtbis,
  FooterMetaEtbisInfo,
  FooterMetaExtra,
  FooterMetaExtraItem,
  FooterMetaExtraLabel,
  FooterMetaHistory,
  FooterMetaHistoryYears,
  FooterMetaInfo,
  FooterSeturLogosImage,
  FooterSeturLogosItem,
  FooterSeturLogosItems,
  FooterSocialMedia,
  FooterSocialMediaContent,
  FooterTextItem,
  FooterTextWrapper,
  imgStyle,
  SocialMediaBox,
  SocialMediaBoxLabel,
  SocialMediaBoxNumberWrapper,
  SocialMediaBoxPhoneNumber,
  SocialMediaBoxText,
  SocialMediaButtonWrapper,
  SocialMediaIconWrapper,
  VisaLogoWrapper,
  FooterWrapper,
} from './FooterStyle';
import { Logo, makeAbsoluteLink, socialMediaImage } from './helpers';
import GeneralLoader from '../GeneralLoader';

const Link = dynamic(() => import('next/link'));
interface DesktopFooterProps {
  data?: FooterViewModel;
  socialMedia?: SocialMediaIconsViewModel[];
  isHomepage?: boolean;
}

const DesktopFooter = ({ data, isHomepage }: DesktopFooterProps) => {
  const { footerRef } = useFooterContext();
  const pathname = usePathname();
  const [searchLoading, setSearchLoading] = useState(false);
  useEffect(() => {
    setSearchLoading(false);
  }, [pathname]);
  return (
    <>
      <FooterSocialMedia>
        <Layout>
          <FooterSocialMediaContent>
            <div>
              {socialMediaImage.map((item, index) => (
                <Link
                  href={item.href || ''}
                  title={item.alt}
                  key={`f-sm-${index}`}
                  onClick={() => {
                    item.alt &&
                      addSocialMediaClickEventsToGTM(
                        'Social Media Platforms',
                        item.alt
                      );
                  }}
                  target='_blank'
                  rel='noopener noreferrer'
                  prefetch={false}
                >
                  <SocialMediaIconWrapper>
                    <Image src={item.image} alt={item.alt} fill />
                  </SocialMediaIconWrapper>
                </Link>
              ))}
            </div>
            {isHomepage ? null : (
              <div>
                <Link
                  href={data?.appStoreUrl || ''}
                  onClick={() => {
                    addSocialMediaClickEventsToGTM(
                      'App Install',
                      'Apple Store'
                    );
                  }}
                  prefetch={false}
                  title='Setur AppStore'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <SocialMediaButtonWrapper>
                    <Image
                      src={AppStoreLogo}
                      width={156}
                      height={42}
                      alt='Setur AppStore'
                    />
                  </SocialMediaButtonWrapper>
                </Link>

                <Link
                  href={data?.googlePlayUrl || ''}
                  onClick={() => {
                    addSocialMediaClickEventsToGTM(
                      'App Install',
                      'Google Play Store'
                    );
                  }}
                  prefetch={false}
                  rel='noopener noreferrer'
                  title='Setur Playstore'
                  target='_blank'
                >
                  <SocialMediaButtonWrapper>
                    <Image
                      src={GooglePlayLogo}
                      width={156}
                      height={42}
                      alt='Setur Playstore'
                    />
                  </SocialMediaButtonWrapper>
                </Link>
              </div>
            )}
          </FooterSocialMediaContent>
        </Layout>
      </FooterSocialMedia>

      <FooterContent ref={footerRef}>
        <Layout>
          <SocialMediaBoxNumberWrapper>
            <BoxText>
              <Link
                href={data?.purposeText?.[0]?.href ?? ''}
                onClick={() => {
                  pathname === data?.purposeText?.[0]?.href
                    ? setSearchLoading(false)
                    : setSearchLoading(true);
                }}
                prefetch={false}
              >
                <BoxLabel>{data?.purposeText?.[0]?.name}</BoxLabel>
              </Link>
            </BoxText>

            <Link
              href={data?.callCenterNumber?.[0]?.href?.split('/')[1] || ''}
              onClick={() => {
                addContactUsClickEventsToGTM('Footer Menu', 'Call Center');
              }}
              prefetch={false}
              title='Setur Telefon'
            >
              <SocialMediaBox>
                <CustomIcon icon={PhoneIcon} fontSize={30} />
                <SocialMediaBoxText>
                  <SocialMediaBoxLabel>Çağrı Merkezi</SocialMediaBoxLabel>
                  <SocialMediaBoxPhoneNumber>
                    {data?.callCenterNumber?.[0]?.name}
                  </SocialMediaBoxPhoneNumber>
                </SocialMediaBoxText>
              </SocialMediaBox>
            </Link>
          </SocialMediaBoxNumberWrapper>

          <FooterListsWrapper>
            <FooterAboutCompany>
              <Link
                href='/'
                prefetch={false}
                title='Setur'
                onClick={() => {
                  setSearchLoading(true);
                  setTimeout(() => {
                    setSearchLoading(false);
                  }, 1000);
                }}
              >
                <FooterLogo>
                  <Image src={SeturLogo} alt='Setur' fill />
                </FooterLogo>
              </Link>

              {data?.corporateColumn?.[0]?.items?.map((item) => (
                <Link
                  href={makeAbsoluteLink(item?.href)}
                  key={item?.name}
                  title={item?.name}
                  onClick={() => {
                    pathname === item?.href
                      ? setSearchLoading(false)
                      : setSearchLoading(true);
                  }}
                  prefetch={false}
                >
                  <FooterListsItemLink>{item?.name} </FooterListsItemLink>
                </Link>
              ))}
            </FooterAboutCompany>

            {data?.items &&
              data?.items?.map((item, index) => (
                <FooterListsItem key={index}>
                  <FooterWrapper>
                    {item.boldedItem?.[0]?.href ? (
                      <Link
                        href={item.boldedItem?.[0]?.href ?? ''}
                        prefetch={false}
                        title={item?.title}
                      >
                        <FooterListsItemTitle
                          onClick={() => {
                            if (item?.title === 'Yurt İçi Oteller')
                              productImpression({
                                item_list_location: 'Category Footer',
                                item_list_id: 'Tüm Yurt İçi Oteller',
                                type: 'hotel',
                              });
                          }}
                        >
                          {item?.title}
                        </FooterListsItemTitle>
                      </Link>
                    ) : (
                      <a
                        href={item.categoryHref ?? ''}
                        title={item.title}
                        onClick={(e) => {
                          if (!item?.categoryHref) {
                            e.preventDefault(); // Prevent navigation
                          }
                        }}
                      >
                        <FooterListsItemTitle>
                          {item?.title}
                        </FooterListsItemTitle>
                      </a>
                    )}
                  </FooterWrapper>
                  {item?.items &&
                    item?.items?.map((subItem, index) => (
                      <Link
                        href={subItem.href ?? ''}
                        key={index}
                        title={subItem.name}
                        prefetch={false}
                        onClick={() => {
                          pathname === subItem.href
                            ? setSearchLoading(false)
                            : setSearchLoading(true);
                        }}
                      >
                        <FooterListsItemLink
                          onClick={() => {
                            addMenuClickEventsToGTM(
                              item.title + ' - ' + subItem.name,
                              'Footer Menu Click'
                            );
                            if (
                              item.title === 'Yurt İçi Oteller' ||
                              item.title === 'Otel Temaları'
                            )
                              productImpression({
                                item_list_location: 'Category Footer',
                                item_list_id: subItem.name,
                                type: 'hotel',
                              });
                            if (item.title === 'Turlar')
                              productImpression({
                                item_list_location: 'Category Footer',
                                item_list_id: subItem.name,
                                type: 'tour',
                              });
                            if (
                              subItem.name === 'Erken Rezervasyon Otelleri' ||
                              subItem.name === 'Yaz Fırsatları'
                            )
                              productImpression({
                                item_list_location: 'Category Footer',
                                item_list_id: subItem.name,
                                type: 'hotel',
                              });
                          }}
                        >
                          {subItem.name}
                        </FooterListsItemLink>
                      </Link>
                    ))}

                  {item.boldedItem?.[0]?.name ? (
                    <FooterListsSumItem
                      href={item.boldedItem?.[0]?.href ?? ''}
                      title={item.boldedItem?.[0]?.name}
                      onClick={() => {
                        pathname === item.boldedItem?.[0]?.href
                          ? setSearchLoading(false)
                          : setSearchLoading(true);
                      }}
                      prefetch={false}
                    >
                      <FooterListsSumItemLink
                        onClick={() => {
                          addMenuClickEventsToGTM(
                            item.title + ' - ' + item.boldedItem?.[0]?.name,
                            'Footer Menu Click'
                          );
                          if (
                            item.boldedItem?.[0]?.name ===
                            'Tüm Yurt İçi Oteller'
                          )
                            productImpression({
                              item_list_location: 'Category Footer',
                              item_list_id: 'Tüm Yurt İçi Oteller',
                              type: 'hotel',
                            });
                        }}
                      >
                        {item.boldedItem?.[0]?.name}
                      </FooterListsSumItemLink>
                      {item.boldedItem?.[0]?.name ? (
                        <CustomIcon
                          icon={RightArrowIcon}
                          fontSize={18}
                          paletteColor='white'
                        />
                      ) : null}
                    </FooterListsSumItem>
                  ) : null}
                </FooterListsItem>
              ))}
          </FooterListsWrapper>

          <FooterSeturLogosItems>
            {Logo.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                title={item.alt}
                target='_blank'
                onClick={() => {
                  item.alt && addMenuClickEventsToGTM(item.alt);
                }}
                prefetch={false}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  ...item.styles,
                }}
              >
                <FooterSeturLogosItem
                  style={{
                    width: item.width,
                    height: item.height,
                  }}
                >
                  <FooterSeturLogosImage src={item.image} alt={item.alt} fill />
                </FooterSeturLogosItem>
              </Link>
            ))}
          </FooterSeturLogosItems>
          <FooterLineDivider />

          <FooterTextWrapper>
            <FooterItem>
              <FooterTextItem maxWidth={880}>
                Belirtilen fiyatlar T.C. ve K.K.T.C. vatandaşları için geçerli
                olup tesisler yabancı uyruklu misafirlerden fiyat farkı talep
                edebilmektedir. Yabancı uyruklu misafirlere uygulanacak
                fiyatları çağrı merkezimizden öğrenebilirsiniz.
              </FooterTextItem>
            </FooterItem>

            <FooterItem>
              <FooterTextItem maxWidth={800}>
                Sitemizde yer alan tesis özellikleri bilgilendirme amaçlıdır,
                hizmet ve kullanım saatleri dönemsel olarak Otel’ler tarafından
                değişitirilebilir.
              </FooterTextItem>
            </FooterItem>
          </FooterTextWrapper>

          <FooterMetaInfo>
            <FooterMetaHistory>
              <Image
                src={KocHolding}
                alt='Koç Holding Logo'
                width={99}
                height={30}
              />

              <FooterMetaHistoryYears>
                Copyright © 2022 Setur Servis Turistik A.Ş. Tüm hakları
                saklıdır. |{' '}
                <Link
                  href='https://kvkk.setur.com.tr/public/document/13/29/pdf/true'
                  target='_blank'
                  rel='noopener noreferrer'
                  prefetch={false}
                >
                  <TermsOfUse>Kullanım Koşulları</TermsOfUse>
                </Link>
              </FooterMetaHistoryYears>
            </FooterMetaHistory>

            <FooterMetaExtra>
              <FooterMetaEtbisInfo id='ETBIS'>
                <FooterMetaEtbis id='7871238370272602'>
                  <Link
                    href='https://www.eticaret.gov.tr/siteprofil/7871238370272602/wwwseturcomtr'
                    target='_blank'
                    rel='noopener noreferrer'
                    prefetch={false}
                    title='Setur Etbis'
                  >
                    <Image
                      {...imgStyle}
                      alt='Setur Etbis'
                      src={ETBIS_INFO}
                      loading='lazy'
                    />
                  </Link>
                </FooterMetaEtbis>
              </FooterMetaEtbisInfo>
              <FooterMetaExtraLabel>
                SETUR SERVİS TURİSTİK A.Ş <br />
                Belge No: 728
              </FooterMetaExtraLabel>

              <FooterMetaExtraItem>
                <Image src={Tursab} alt='tursab logo' width={75} height={30} />
              </FooterMetaExtraItem>
              <VisaLogoWrapper>
                <Image priority={false} src={VisaLogo} alt='visa card logo ' />
              </VisaLogoWrapper>
            </FooterMetaExtra>
          </FooterMetaInfo>
        </Layout>
        {searchLoading ? <GeneralLoader shouldScroll={false} /> : null}
      </FooterContent>
    </>
  );
};

export default DesktopFooter;
